import { createTheme, ThemeOptions } from '@mui/material';
import { colors } from './colors';

/**
 * Main application theme configuration
 * Uses Deep Indigo and Electric Blue color scheme
 */

export const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#4A3AFF',        // Deep Indigo
      light: '#6A5BFF',       // Soft Indigo (Primary Hover)
      dark: '#3A2AEF',        // Darker indigo
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#5C9DFF',        // Electric Blue
      light: '#7CB3FF',       // Light Electric Blue
      dark: '#3C7DDF',        // Dark Electric Blue
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F7F9FC',     // Off-White
      paper: '#FFFFFF',       // White (Card/Surface)
    },
    text: {
      primary: '#1E2230',     // Dark Slate
      secondary: '#6B7280',   // Cool Gray
    },
    success: {
      main: '#38C683',        // Green
      light: '#58D6A3',       // Light Green
      dark: '#28B673',        // Dark Green
    },
    warning: {
      main: '#FFC557',        // Amber
      light: '#FFD577',       // Light Amber
      dark: '#EFB547',        // Dark Amber
    },
    error: {
      main: '#FF6B6B',        // Red
      light: '#FF8B8B',       // Light Red
      dark: '#EF5B5B',        // Dark Red
    },
    info: {
      main: '#5C9DFF',        // Electric Blue (same as secondary)
      light: '#7CB3FF',       // Light Electric Blue
      dark: '#3C7DDF',        // Dark Electric Blue
    },
    divider: '#E5E7EB',       // Light Gray (Border/Divider)
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 24px',
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.9375rem',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1rem',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
        contained: {
          color: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
          '&:hover': {
            color: '#FFFFFF',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.3)',
          },
          '&:active': {
            color: '#FFFFFF',
          },
          '&:focus': {
            color: '#FFFFFF',
          },
        },
        containedPrimary: {
          color: '#FFFFFF',
          background: 'linear-gradient(135deg, #4A3AFF 0%, #6A5BFF 100%)',
          boxShadow: '0 4px 14px rgba(74, 58, 255, 0.3)',
          '&:hover': {
            color: '#FFFFFF',
            background: 'linear-gradient(135deg, #3A2AEF 0%, #4A3AFF 100%)',
            boxShadow: '0 6px 20px rgba(74, 58, 255, 0.4)',
          },
        },
        containedSecondary: {
          color: '#FFFFFF',
          background: 'linear-gradient(135deg, #5C9DFF 0%, #7CB3FF 100%)',
          boxShadow: '0 4px 14px rgba(92, 157, 255, 0.3)',
          '&:hover': {
            color: '#FFFFFF',
            background: 'linear-gradient(135deg, #3C7DDF 0%, #5C9DFF 100%)',
            boxShadow: '0 6px 20px rgba(92, 157, 255, 0.4)',
          },
        },
        containedSuccess: {
          color: '#FFFFFF',
          background: 'linear-gradient(135deg, #38C683 0%, #58D6A3 100%)',
          boxShadow: '0 4px 14px rgba(56, 198, 131, 0.3)',
          '&:hover': {
            color: '#FFFFFF',
            background: 'linear-gradient(135deg, #28B673 0%, #38C683 100%)',
            boxShadow: '0 6px 20px rgba(56, 198, 131, 0.4)',
          },
        },
        containedError: {
          color: '#FFFFFF',
          background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8B8B 100%)',
          boxShadow: '0 4px 14px rgba(255, 107, 107, 0.3)',
          '&:hover': {
            color: '#FFFFFF',
            background: 'linear-gradient(135deg, #EF5B5B 0%, #FF6B6B 100%)',
            boxShadow: '0 6px 20px rgba(255, 107, 107, 0.4)',
          },
        },
        containedWarning: {
          color: '#FFFFFF',
          background: 'linear-gradient(135deg, #FFC557 0%, #FFD577 100%)',
          boxShadow: '0 4px 14px rgba(255, 197, 87, 0.3)',
          '&:hover': {
            color: '#FFFFFF',
            background: 'linear-gradient(135deg, #EFB547 0%, #FFC557 100%)',
            boxShadow: '0 6px 20px rgba(255, 197, 87, 0.4)',
          },
        },
        containedInfo: {
          color: '#FFFFFF',
          background: 'linear-gradient(135deg, #5C9DFF 0%, #7CB3FF 100%)',
          boxShadow: '0 4px 14px rgba(92, 157, 255, 0.3)',
          '&:hover': {
            color: '#FFFFFF',
            background: 'linear-gradient(135deg, #3C7DDF 0%, #5C9DFF 100%)',
            boxShadow: '0 6px 20px rgba(92, 157, 255, 0.4)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: 'rgba(74, 58, 255, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
          border: '1px solid #E5E7EB',
          background: '#FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 10px 40px rgba(74, 58, 255, 0.12)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#4A3AFF',
          boxShadow: '0 4px 20px rgba(74, 58, 255, 0.15)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          transition: 'all 0.2s ease',
          fontSize: '0.8125rem',
        },
        filled: {
          '&.MuiChip-colorPrimary': {
            background: 'linear-gradient(135deg, #4A3AFF 0%, #6A5BFF 100%)',
            color: '#FFFFFF',
          },
          '&.MuiChip-colorSecondary': {
            background: 'linear-gradient(135deg, #5C9DFF 0%, #7CB3FF 100%)',
            color: '#FFFFFF',
          },
          '&.MuiChip-colorSuccess': {
            background: 'linear-gradient(135deg, #38C683 0%, #58D6A3 100%)',
            color: '#FFFFFF',
          },
          '&.MuiChip-colorWarning': {
            background: 'linear-gradient(135deg, #FFC557 0%, #FFD577 100%)',
            color: '#1E2230',
          },
          '&.MuiChip-colorError': {
            background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8B8B 100%)',
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease',
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderWidth: 1.5,
              borderColor: '#E5E7EB',
              transition: 'all 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: '#4A3AFF',
              borderWidth: 1.5,
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4A3AFF',
              borderWidth: 2,
              boxShadow: '0 0 0 3px rgba(74, 58, 255, 0.1)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
        },
        elevation2: {
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
        },
        elevation3: {
          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);

/**
 * Create a tenant-specific theme (if needed for multi-tenant overrides)
 */
export const createTenantTheme = (tenantColors?: {
  primaryColor?: string;
  secondaryColor?: string;
}) => {
  if (!tenantColors || !tenantColors.primaryColor) {
    return theme;
  }

  return createTheme({
    ...themeOptions,
    palette: {
      ...themeOptions.palette,
      primary: {
        main: tenantColors.primaryColor,
        light: tenantColors.primaryColor,
        dark: tenantColors.secondaryColor || tenantColors.primaryColor,
        contrastText: colors.white,
      },
    },
  });
};
